/*  iceberg-ui  */
import { Box, Card, Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Divider, Typography } from '@mui/material';

/* Shared */
import {
    AppNameLinkCell,
    HMSCell,
    LifecycleCell,
    NumberCell,
    PercentageCell
} from 'application/Shared/Components/Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { ColumnDef } from 'application/Shared/Components/Table/types';
import { TABLENAMES } from 'application/Shared/Constants';

/*  React  */
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ApplicationOverlapCategoryBreakdownData,
    SubCategoryBreakdown,
    TenantAppWithUsage
} from '../types';

const columns: ColumnDef<TenantAppWithUsage, string>[] = [
    {
        accessorKey: 'application.name',
        cell: (props) => <AppNameLinkCell {...props} withCategory={false} />,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableHiding: false,
        header: Header,
        id: 'apps',
        name: 'Common.application',
        size: 300,
        testId: 'application.name'
    },
    {
        accessorKey: 'status',
        cell: LifecycleCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'status',
        name: 'Common.lifecycleStage',
        size: 175,
        testId: 'lifecycleStage'
    },
    {
        accessorKey: 'userCount',
        cell: NumberCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'userCount',
        name: 'Common.users',
        size: 100,
        testId: 'userCount'
    },
    {
        accessorKey: 'usage',
        cell: HMSCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'usage',
        name: 'Common.usage',
        size: 100,
        testId: 'usage'
    },
    {
        accessorKey: 'usagePercentage',
        cell: PercentageCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'usagePercentage',
        name: 'Common.percentage',
        size: 100,
        testId: 'usagePercentage'
    }
];

export const SubCategoryUsageBreakdownTable = ({
    categoryName,
    subCategoryBreakdown
}: {
    categoryName: string;
    subCategoryBreakdown: SubCategoryBreakdown;
}): ReactElement => {
    const { subCategoryName, tenantAppsWithUsage } = subCategoryBreakdown;

    return (
        <ManagedTableV2ContextProvider
            rowsPerPage={-1}
            columns={columns}
            ignoreSearchParams
            rows={tenantAppsWithUsage}
            mode="client"
            searchEnabled={false}
            sortDirection="ASC"
            sortKey="apps"
            tableName={
                TABLENAMES.REPORT_APPLICATION_OVERLAP_SUB_CATEGORY_USAGE_BREAKDOWN
            }
        >
            <Card sx={{ marginBottom: '32px' }}>
                <Flex sx={{ margin: '16px 16px 0' }}>
                    <Typography sx={{ marginRight: '4px' }} variant="subtitle1">
                        {categoryName}:
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {subCategoryName}
                    </Typography>
                </Flex>
                {/*
                    border radius fixes visual bug where the table doesn't have rounded corners
                    and causes a break in the bottom corners of the Card's border that contains it
                    TODO: Maybe there's a better way to fix this???
                */}
                <Box sx={{ borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                    <ManagedTableV2UI
                        titleDef={{
                            titleKey: 'Common.applications'
                        }}
                    />
                </Box>
            </Card>
        </ManagedTableV2ContextProvider>
    );
};

const SubCategoryUsageBreakdown = (props: {
    categoryBreakdown: ApplicationOverlapCategoryBreakdownData;
}) => {
    const { categoryBreakdown } = props;

    const { subCategories, categoryName } = categoryBreakdown;

    return (
        <Box mt="32px" width="100%">
            <Typography variant="h4">{categoryName}</Typography>
            <Divider
                sx={{
                    borderBottomWidth: '4px',
                    borderColor: 'border.darkerDivider',
                    marginBottom: '24px',
                    marginTop: '8px'
                }}
                data-trackid="divider1"
            />

            {subCategories.map((subCategory: SubCategoryBreakdown) => (
                <SubCategoryUsageBreakdownTable
                    key={subCategory.subCategoryName}
                    categoryName={categoryName}
                    subCategoryBreakdown={subCategory}
                />
            ))}
        </Box>
    );
};

interface CategoryUsageBreakdownProps {
    categoryUsageBreakdownData: ApplicationOverlapCategoryBreakdownData[];
}

export const CategoryUsageBreakdown = (props: CategoryUsageBreakdownProps) => {
    const { t } = useTranslation();

    const { categoryUsageBreakdownData } = props;

    return (
        <Box sx={{ marginBottom: '32px' }}>
            <Typography variant="h3" sx={{ marginBottom: '24px' }}>
                {t('Reports.applicationOverlapReport.categoryBreakdown.title')}
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: '16px' }}>
                {t(
                    'Reports.applicationOverlapReport.categoryBreakdown.description'
                )}
            </Typography>

            {categoryUsageBreakdownData.map(
                (
                    categoryBreakdown: ApplicationOverlapCategoryBreakdownData
                ) => (
                    <SubCategoryUsageBreakdown
                        key={categoryBreakdown.categoryName}
                        categoryBreakdown={categoryBreakdown}
                    />
                )
            )}
        </Box>
    );
};
